import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { HeaderCategory } from "../../../../services/headerCategories";

type EditableHeaderCategory = Omit<HeaderCategory, "id"> & { id?: string };

interface HeaderCategoriesDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  editingHeaderCategory: EditableHeaderCategory | null;
  setEditingHeaderCategory: React.Dispatch<React.SetStateAction<EditableHeaderCategory | null>>;
}

const HeaderCategoriesDialog: React.FC<HeaderCategoriesDialogProps> = ({
  open,
  onClose,
  onSave,
  editingHeaderCategory,
  setEditingHeaderCategory,
}) => {
  const { t } = useTranslation();

  const handleChange = (field: keyof EditableHeaderCategory, value: any) => {
    if (!editingHeaderCategory) return;
    setEditingHeaderCategory({
      ...editingHeaderCategory,
      [field]: value,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          width: { xs: "95%", sm: "90%", md: "80%" },
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
        {editingHeaderCategory?.id
          ? t("admin.contentManagement.editHeaderCategory")
          : t("admin.contentManagement.addHeaderCategory")}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label={t("admin.contentManagement.fields.key")}
            fullWidth
            value={editingHeaderCategory?.key || ""}
            onChange={(e) => handleChange("key", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.fields.labelEn")}
            fullWidth
            value={editingHeaderCategory?.label_en || ""}
            onChange={(e) => handleChange("label_en", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.fields.labelAr")}
            fullWidth
            value={editingHeaderCategory?.label_ar || ""}
            onChange={(e) => handleChange("label_ar", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.fields.labelFr")}
            fullWidth
            value={editingHeaderCategory?.label_fr || ""}
            onChange={(e) => handleChange("label_fr", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.fields.displayOrder")}
            fullWidth
            type="number"
            value={editingHeaderCategory?.display_order || 0}
            onChange={(e) => handleChange("display_order", parseInt(e.target.value))}
          />
          <FormControlLabel
            control={
              <Switch
                checked={editingHeaderCategory?.is_active ?? true}
                onChange={(e) => handleChange("is_active", e.target.checked)}
              />
            }
            label={t("admin.contentManagement.active")}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose}>
          {t("admin.contentManagement.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          sx={{
            background: "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
          }}
        >
          {t("admin.contentManagement.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HeaderCategoriesDialog;